import { FiltersMap, KeyedItem } from '@wix/bex-core';
import { ToolbarBIReporter } from '../../state/ToolbarBIReporter';
import { ActionsCellProps, SecondaryAction } from '../ActionsCell';
import { action } from 'mobx';
import { createElement } from 'react';
import { ExtensionMenuItemsState } from '../../state';

const calcCtaCount = (actionCellProps: ActionsCellProps) => {
  const { primaryAction, secondaryActions } = actionCellProps;
  const primaryActionLength = primaryAction ? 1 : 0;
  const numCtas = (secondaryActions?.length || 0) + primaryActionLength;
  return numCtas;
};

export const createSecondaryActionCellPropWithBi = <T, F extends FiltersMap>({
  toolbarBIReporter,
  actionCellProps,
  keyedItem,
  extensionMenuItems,
}: {
  toolbarBIReporter: ToolbarBIReporter<T, F>;
  actionCellProps: ActionsCellProps;
  keyedItem: KeyedItem<T>;
  extensionMenuItems?: ExtensionMenuItemsState;
}): SecondaryAction[] => {
  const { primaryAction, secondaryActions = [] } = actionCellProps;
  const primaryActionLength = primaryAction ? 1 : 0;

  const moreSecondaryItems: SecondaryAction[] =
    extensionMenuItems?.menuItems.map((menuItem) => {
      return {
        biName: menuItem.biName,
        dataHook: menuItem.dataHook,
        onClick: () => {
          menuItem.onClick({ selectedId: keyedItem.id });
        },
        text: menuItem.text,
        icon: menuItem.prefixIcon || createElement('div'),
        componentId: menuItem.componentId,
        appId: menuItem.appId,
      };
    }) || [];

  return (
    [...secondaryActions, ...moreSecondaryItems].map((_action, index) => {
      const biIndex = index + 1;

      if ('onClick' in _action) {
        const onClick = action(() => {
          toolbarBIReporter.ctaClicked({
            location: 'actionCell',
            ctaIndex: primaryActionLength + biIndex,
            ctaName: _action?.biName || _action?.text || 'secondaryAction',
            moreActionsIndex: biIndex,
            numCtas: calcCtaCount(actionCellProps),
            itemIndex: keyedItem.index,
            itemId: keyedItem.id,
            componentId: _action.componentId,
            appId: _action.appId,
          });
          _action.onClick?.();
        });
        const { biName: _biName, ...wsrSecondaryAction } = _action;
        return { ...wsrSecondaryAction, onClick };
      }

      return _action;
    }) || []
  );
};

export const createPrimaryActionCellPropsWithBI = <T, F extends FiltersMap>(
  toolbarBIReporter: ToolbarBIReporter<T, F>,
  actionCellProps: ActionsCellProps,
  keyedItem: KeyedItem<T>,
) => {
  const { primaryAction } = actionCellProps;

  if (primaryAction) {
    const onClick = action(() => {
      toolbarBIReporter.ctaClicked({
        location: 'actionCell',
        ctaIndex: 1,
        ctaName:
          primaryAction?.biName || primaryAction?.text || 'primaryAction',
        moreActionsIndex: 0,
        numCtas: calcCtaCount(actionCellProps),
        itemIndex: keyedItem.index,
        itemId: keyedItem.id,
      });
      primaryAction?.onClick();
    });
    const { biName, ...wsrPrimaryAction } = primaryAction;
    return { ...wsrPrimaryAction, onClick };
  }
  return primaryAction;
};

export const reportOnPopoverMenuShow = <T, F extends FiltersMap>(
  toolbarBIReporter: ToolbarBIReporter<T, F>,
  actionCellProps: ActionsCellProps,
  keyedItem: KeyedItem<T>,
) => {
  toolbarBIReporter.ctaClicked({
    location: 'actionCell',
    ctaIndex: 0,
    ctaName: 'More Actions Menu',
    numCtas: calcCtaCount(actionCellProps),
    itemId: keyedItem.id,
    itemIndex: keyedItem.index,
  });
};
